import useUserStore from '@/stores/UserStore';
import {
  CheckSquare,
  Loader2,
  RefreshCw,
  Send,
  SkipForward,
  Square,
  X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

import FollowerMessageEditor from './FollowerMessageEditor';

const SelfServiceCard = ({
  follower,
  isSelected,
  isMultiSelectMode,
  isEditing,
  editValue,
  isRemoving,
  loadingAction,
  activeFilter,
  onSelect,
  onEdit,
  onSave,
  onCancel,
  onStatusChange,
  onRegenerate,
  inViewRef,
  isLastItem,
  hasNextPage,
}) => {
  const { isDemoAccount } = useUserStore((state) => ({
    isDemoAccount: state.isDemoAccount,
  }));

  const getScoreColorClass = (score) => {
    if (score == null) return 'bg-muted text-foreground';
    if (score < 20) return 'bg-red-500 text-white';
    if (score < 40) return 'bg-orange-500 text-white';
    if (score < 60) return 'bg-yellow-500 text-black';
    if (score < 80) return 'bg-lime-500 text-black';
    return 'bg-green-600 text-white';
  };
  const handleCardClick = (e) => {
    // Only handle card clicks in multi-select mode
    if (!isMultiSelectMode) return;

    // Don't trigger if clicking on interactive elements
    if (
      e.target.closest('button') ||
      e.target.closest('input') ||
      e.target.closest('textarea')
    ) {
      return;
    }

    onSelect(follower._id);
  };

  return (
    <Card
      ref={isLastItem && hasNextPage ? inViewRef : null}
      onClick={handleCardClick}
      className={cn(
        'relative h-full transition-all duration-300 ease-in-out hover:shadow-md',
        isSelected && 'bg-primary/5 ring-2 ring-primary/50',
        isMultiSelectMode && 'cursor-pointer',
        isRemoving && 'pointer-events-none scale-95 opacity-0',
        !isDemoAccount &&
          follower.self_service_action === 'sent' &&
          'border-green-300 bg-green-100 dark:border-green-700 dark:bg-green-900/30',
        !isDemoAccount &&
          follower.self_service_action === 'skipped' &&
          'border-yellow-300 bg-yellow-100 dark:border-yellow-700 dark:bg-yellow-900/30',
        !isDemoAccount &&
          follower.self_service_action === 'not_found' &&
          'border-red-300 bg-red-100 dark:border-red-700 dark:bg-red-900/30'
      )}
    >
      <CardContent className='relative flex h-full flex-col p-3'>
        {/* Selection checkbox - Top right corner */}
        {isMultiSelectMode && (
          <div className='absolute right-2 top-2'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => onSelect(follower._id)}
              className='h-6 w-6 p-0 hover:bg-primary/10'
            >
              {isSelected ? (
                <CheckSquare className='h-4 w-4 text-primary' />
              ) : (
                <Square className='h-4 w-4 text-muted-foreground' />
              )}
            </Button>
          </div>
        )}

        {/* Profile Section - Top */}
        <div className='mb-3 flex items-center gap-3'>
          <Avatar className='h-10 w-10'>
            <AvatarImage />
            <AvatarFallback>
              {follower?.full_name?.charAt(0).toUpperCase() || ''}
            </AvatarFallback>
          </Avatar>

          <div className='min-w-0 flex-1'>
            <div className='flex items-center justify-between'>
              <div className='min-w-0 flex-1'>
                <h3 className='max-w-72 truncate text-sm font-semibold'>
                  {follower.full_name}
                </h3>
                <div className='mt-1 flex items-center gap-1 text-xs text-muted-foreground'>
                  <a
                    href={`https://instagram.com/${follower.username}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-primary transition-colors hover:underline'
                  >
                    @{follower.username}
                  </a>
                  {!isDemoAccount && (
                    <>
                      <span>|</span>
                      <span>Score:</span>
                      <span className='font-semibold text-primary'>
                        {follower.lead_score}
                      </span>
                    </>
                  )}
                </div>
                {/* Followers and Following counts */}
                {(follower.followers > 0 || follower.following > 0) && (
                  <div className='mt-1 flex items-center gap-2 text-xs text-muted-foreground'>
                    {follower.followers > 0 && (
                      <span>
                        <strong>{follower.followers}</strong> Followers
                      </span>
                    )}
                    {follower.following > 0 && (
                      <span>
                        <strong>{follower.following}</strong> Following
                      </span>
                    )}
                  </div>
                )}
              </div>
              {/* Regenerate icon - right side - hide in multi-select mode */}
              {follower.message_older_than_prompt && !isMultiSelectMode && (
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() => onRegenerate(follower._id)}
                  className='h-9 w-9 self-start text-muted-foreground hover:text-primary'
                  title='Regenerate personalized message'
                >
                  <RefreshCw className='h-4 w-4' />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Demo score badge - right side */}
        {isDemoAccount && (
          <div
            className={cn(
              'absolute right-3 flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold shadow-md',
              getScoreColorClass(follower.lead_score)
            )}
            title={`Score: ${follower.lead_score ?? '-'}`}
          >
            {follower.lead_score ?? '-'}
          </div>
        )}

        {/* Bio Section */}
        {follower.biography && (
          <div className='mb-3'>
            <p className='line-clamp-3 text-xs text-muted-foreground'>
              {follower.biography}
            </p>
          </div>
        )}

        {/* Message Section - Bottom */}
        <div className='mb-3 mt-auto'>
          {activeFilter === 'history' ? (
            /* Read-only message display for history view */
            <div className='rounded-md border bg-muted/50 p-3'>
              <p className='text-sm text-muted-foreground'>
                {follower.opens_message?.text || 'No message available'}
              </p>
            </div>
          ) : (
            <FollowerMessageEditor
              value={isEditing ? editValue : follower.opens_message?.text || ''}
              onChange={(e) => onEdit(follower, e.target.value)}
              onSave={() => onSave(follower._id)}
              onCancel={() => onCancel()}
              onEdit={() =>
                onEdit(follower, follower.opens_message?.text || '')
              }
              isEditing={isEditing}
              followerId={follower._id}
            />
          )}
        </div>

        {/* Action Buttons - Bottom */}
        {!isMultiSelectMode && !isDemoAccount && activeFilter !== 'history' && (
          <div className='flex justify-end gap-2'>
            {/* Show Mark as Sent button only if follower's self_service_action is not 'sent' */}
            {follower.self_service_action !== 'sent' && (
              <Button
                size='xs'
                onClick={() => onStatusChange(follower._id, 'mark_sent')}
                className='h-7 border-green-600 bg-green-600 px-2 text-xs text-white hover:bg-green-700'
                disabled={loadingAction === 'mark_sent'}
              >
                {loadingAction === 'mark_sent' ? (
                  <Loader2 className='mr-1 h-3 w-3 animate-spin' />
                ) : (
                  <Send className='mr-1 h-3 w-3' />
                )}
                {loadingAction === 'mark_sent'
                  ? 'Processing...'
                  : 'Mark as Sent'}
              </Button>
            )}

            {/* Show Skip button only if follower's self_service_action is not 'skipped' */}
            {follower.self_service_action !== 'skipped' && (
              <Button
                variant='outline'
                size='xs'
                onClick={() => onStatusChange(follower._id, 'skip')}
                className='h-7 border-yellow-500 px-2 text-xs text-yellow-600 hover:border-yellow-600 hover:bg-yellow-50 dark:border-yellow-400 dark:text-yellow-400 dark:hover:bg-yellow-950'
                disabled={loadingAction === 'skip'}
              >
                {loadingAction === 'skip' ? (
                  <Loader2 className='mr-1 h-3 w-3 animate-spin' />
                ) : (
                  <SkipForward className='mr-1 h-3 w-3' />
                )}
                {loadingAction === 'skip' ? 'Processing...' : 'Skip'}
              </Button>
            )}

            {/* Show Not Found button only if follower's self_service_action is not 'not_found' */}
            {follower.self_service_action !== 'not_found' && (
              <Button
                variant='outline'
                size='xs'
                onClick={() => onStatusChange(follower._id, 'not_found')}
                className='h-7 border-red-500 px-2 text-xs text-red-600 hover:border-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-950'
                disabled={loadingAction === 'not_found'}
              >
                {loadingAction === 'not_found' ? (
                  <Loader2 className='mr-1 h-3 w-3 animate-spin' />
                ) : (
                  <X className='mr-1 h-3 w-3' />
                )}
                {loadingAction === 'not_found' ? 'Processing...' : 'Not Found'}
              </Button>
            )}
          </div>
        )}

        {/* History Status Display */}
        {activeFilter === 'history' && follower.opens_message && (
          <div className='mt-2 flex justify-end'>
            <div
              className={cn(
                'rounded-full px-2 py-1 text-xs font-medium',
                follower.self_service_action === 'sent' &&
                  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                follower.self_service_action === 'skipped' &&
                  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
                follower.self_service_action === 'not_found' &&
                  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              )}
            >
              {follower.self_service_action === 'sent' && 'Sent'}
              {follower.self_service_action === 'skipped' && 'Skipped'}
              {follower.self_service_action === 'not_found' && 'Not Found'}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SelfServiceCard;
